import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Bounds } from '@react-three/drei';
import * as THREE from 'three';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

import { getProductModelObjectUrl } from '../../edit-product-v3/api/productV2Api';

interface ProductPoseWidgetProps {
    /** GLB / GLTF model URL for the linked furniture product. */
    modelPath: string;
    /**
     * Product UUID. When set, the model is fetched DECRYPTED from the
     * backoffice-only endpoint (the model is encrypted at rest) instead of
     * loading `modelPath` directly.
     */
    productUuid?: string;
    /**
     * URL of the previously uploaded pose snapshot, if any. Shown as a
     * "Captured" badge + lets the parent skip re-upload when unchanged.
     */
    poseSnapshotUrl?: string;
    /**
     * Invoked with a File built from the current R3F canvas frame
     * whenever the orbit settles (user stops dragging). Parent uploads
     * the file and writes the resulting URL back via onSnapshotUpdated.
     */
    onCapture: (file: File) => Promise<string>;
    /** Parent persists the uploaded URL onto the recipe node's inputs. */
    onSnapshotUpdated: (url: string) => void;
}

/**
 * Inline 3D rotation widget used by Variation · 3D rotation mode.
 *
 * Renders the furniture product's GLB with orbit-only controls — the
 * user drags directly on the 3D piece to rotate it into the pose they
 * want. Each time the orbit settles we capture the current frame as a
 * PNG, upload it, and persist the URL on the recipe node's inputs so
 * the worker can use it as the pose reference for Flux Klein edit.
 *
 * Pipeline contract on Generate:
 *   IMAGE 1 = this captured pose snapshot (geometry / rotation anchor)
 *   IMAGE 2 = the parent generation asset    (material / identity anchor)
 *   LoRA    = disabled (the consistency LoRA fights the re-pose)
 */
export const ProductPoseWidget: React.FC<ProductPoseWidgetProps> = ({
    modelPath,
    productUuid,
    poseSnapshotUrl,
    onCapture,
    onSnapshotUpdated,
}) => {
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState(!poseSnapshotUrl);
    // The actually-loadable model URL: when productUuid is set, the decrypted
    // model is fetched as an object URL; otherwise modelPath is used directly.
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!modelPath) { setResolvedUrl(null); return; }
        if (!productUuid) { setResolvedUrl(modelPath); return; }
        let objectUrl: string | null = null;
        let cancelled = false;
        setResolvedUrl(null);
        getProductModelObjectUrl(productUuid)
            .then((u) => {
                if (cancelled) { URL.revokeObjectURL(u); return; }
                objectUrl = u;
                setResolvedUrl(u);
            })
            .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load 3D model'); });
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [modelPath, productUuid]);

    const captureAndUpload = async () => {
        const renderer = rendererRef.current;
        if (!renderer) return;
        setUploading(true);
        setError(null);
        try {
            // Force a fresh render so we capture the CURRENT frame, not
            // whatever the last render flush put in the back buffer.
            renderer.render(renderer.domElement as any as THREE.Scene, renderer as any);
        } catch { /* render call above is a no-op safety; ignore */ }
        try {
            const blob: Blob | null = await new Promise((resolve) => {
                renderer.domElement.toBlob((b) => resolve(b), 'image/png', 0.95);
            });
            if (!blob) throw new Error('Canvas toBlob returned null');
            const file = new File([blob], `pose-${Date.now()}.png`, { type: 'image/png' });
            const url = await onCapture(file);
            onSnapshotUpdated(url);
            setDirty(false);
        } catch (err: any) {
            setError(err?.message || 'Failed to capture pose');
        } finally {
            setUploading(false);
        }
    };

    if (!modelPath) {
        return (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground italic">
                This product doesn't have a 3D model — 3D rotation is unavailable.
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div
                className="relative aspect-square w-full cursor-grab overflow-hidden rounded-lg border active:cursor-grabbing"
                // Matches the WebGL clear colour set below — data, not theme.
                style={{ backgroundColor: '#F8F9FB' }}
                onMouseDown={(e) => e.stopPropagation()}
                onWheel={(e) => e.stopPropagation()}
            >
                <Canvas
                    gl={{ preserveDrawingBuffer: true, antialias: true }}
                    camera={{ position: [2.5, 1.8, 2.5], fov: 30 }}
                    onCreated={({ gl }) => {
                        rendererRef.current = gl;
                        gl.setClearColor(0xF8F9FB, 1);
                    }}
                >
                    <ambientLight intensity={0.7} />
                    <directionalLight position={[5, 8, 5]} intensity={1.2} />
                    <directionalLight position={[-5, 3, -5]} intensity={0.4} />
                    <Suspense fallback={null}>
                        {resolvedUrl && (
                            <Bounds fit clip observe margin={1.1}>
                                <Model url={resolvedUrl} onLoaded={() => setDirty(true)} />
                            </Bounds>
                        )}
                    </Suspense>
                    <OrbitControls
                        enablePan={false}
                        enableZoom={false}
                        enableDamping
                        dampingFactor={0.12}
                        onEnd={() => setDirty(true)}
                    />
                </Canvas>

                {!resolvedUrl && !error && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    </div>
                )}

                {!dirty && poseSnapshotUrl && (
                    // 3D viewport overlay — physical corner, LTR coordinate space.
                    <div className="absolute top-2 right-2 rounded-md border border-success/40 bg-background/90 px-2 py-0.5 text-[10px] font-medium tracking-[0.05em] text-success uppercase">
                        Pose ready ✓
                    </div>
                )}
            </div>

            <Button
                variant="outline"
                onClick={captureAndUpload}
                disabled={uploading}
                className={cn(
                    'h-auto w-full gap-2 px-2.5 py-2 text-sm',
                    !dirty && !uploading && 'text-muted-foreground',
                )}
            >
                {uploading ? (
                    <>
                        <Loader2 className="size-3 animate-spin" />
                        Saving pose…
                    </>
                ) : dirty ? (
                    poseSnapshotUrl ? 'Save new pose' : 'Save this pose'
                ) : (
                    'Pose saved — rotate to change'
                )}
            </Button>

            {error && (
                <p className="text-center text-xs text-destructive">{error}</p>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// GLB loader — clones the drei cache so concurrent mounts don't mutate
// the same scene graph.
// ═══════════════════════════════════════════════════════════════════════

interface ModelProps {
    url: string;
    onLoaded?: () => void;
}

const Model: React.FC<ModelProps> = ({ url, onLoaded }) => {
    const { scene } = useGLTF(url) as any;
    const cloned = useMemo(() => scene.clone(true), [scene]);
    useEffect(() => {
        onLoaded?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cloned]);
    return <primitive object={cloned} />;
};
